import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { FilterInput } from '../filter-input'
import { FilterDate } from '../filter-date'
import { FilterSwitch } from '../filter-switch'
import { FilterAutocomplete } from '../filter-autocomplete'
import { FilterBasicInput } from '../filter-basic-input'

/**
 * @typedef {object} FilterURLProps
 * @property {'input' | 'autocomplete' | 'date' | 'switch' | 'basic-input'} [filterType='input'] - El tipo de componente de filtro a renderizar.
 * @property {React.ReactElement} [filterComponent] - Un componente de filtro personalizado ya ensamblado. Si se proporciona, se renderiza directamente sin gestión automática de URL.
 * @property {string} name - El nombre del parámetro de filtro en la URL. Requerido para la gestión automática de URL.
 * @property {string} [label] - La etiqueta visible del filtro.
 * @property {any} [default] - El valor por defecto del filtro si no hay valor en la URL.
 * @property {string[]} [cleanup] - Un array de nombres de otros parámetros de URL que deben eliminarse cuando este filtro cambia.
 * @property {boolean} [cleanSelf] - Si es `true` y el filtro es un switch que se desactiva, el parámetro se elimina de la URL en lugar de establecerse en `false`.
 * @property {string} [minDateField] - (Solo para `filterType='date'`) El nombre del parámetro de URL que contiene la fecha mínima permitida.
 * @property {string} [maxDateField] - (Solo para `filterType='date'`) El nombre del parámetro de URL que contiene la fecha máxima permitida.
 */

/**
 * Un componente de orden superior que envuelve diferentes tipos de filtros y gestiona automáticamente
 * su sincronización con los parámetros de búsqueda de la URL.
 * 
 * Proporciona una interfaz unificada para trabajar con filtros de diferentes tipos (input, autocomplete,
 * date, switch, basic-input), gestionando automáticamente:
 * - La lectura y escritura de valores en la URL
 * - El almacenamiento en localStorage para persistencia entre sesiones
 * - La limpieza de filtros relacionados cuando un filtro cambia
 * - El reseteo de la paginación a la página 1 cuando se aplica un filtro
 * 
 * **Nota:** Este componente depende de `react-router-dom` para la navegación y gestión de URL.
 * 
 * @component
 * @example
 * // Uso básico con un filtro de autocompletado
 * <FilterURL
 *   filterType="autocomplete"
 *   name="category"
 *   label="Categoría"
 *   values={[
 *     { key: 1, value: 'Electrónica' },
 *     { key: 2, value: 'Hogar' },
 *   ]}
 *   multiple
 * />
 * 
 * @example
 * // Filtro de fecha con restricciones dinámicas
 * <FilterURL
 *   filterType="date"
 *   name="startDate"
 *   label="Fecha de inicio"
 *   maxDateField="endDate"
 * />
 * 
 * @example
 * // Switch que limpia otros filtros al activarse
 * <FilterURL
 *   filterType="switch"
 *   name="showAll"
 *   label="Mostrar todos"
 *   cleanup={['category', 'status']}
 *   cleanSelf
 * />
 * 
 * @example
 * // Componente personalizado sin gestión automática de URL
 * <FilterURL
 *   filterComponent={<MyCustomFilter value={customValue} onChange={handleChange} />}
 * />
 * 
 * @param {FilterURLProps} props - Las props del componente FilterURL.
 */
export const FilterURL = ({ filterType = 'input', filterComponent, ...item }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const appName = location.pathname.substring(location.pathname.lastIndexOf('/') + 1)
  const currentSearch = new URLSearchParams(location.search)

  useEffect(() => {
    const value = localStorage.getItem(appName + item.name)
    if (value) {
      currentSearch.set(item.name, value)
    }
  }, [])

  //Url managing functions (added conveniently to not yet assembled filters)
  const handleChangeFilter = (event, cleanup) => {
    currentSearch.set('_pagina', 1)
    if (event.target.value) {
      if (
        event.target.value === '' ||
        (Array.isArray(event.target.value) && event.target.value.length === 0)
      ) {
        currentSearch.delete(event.target.name)
        localStorage.removeItem(appName + event.target.name)
      } else {
        currentSearch.set(
          event.target.name,
          Array.isArray(event.target.value) ? event.target.value.join(',') : event.target.value
        )
        localStorage.setItem(appName + event.target.name, event.target.value)
        if (cleanup) {
          cleanup.forEach((field) => {
            currentSearch.delete(field)
            localStorage.removeItem(appName + field)
          })
        }
      }
    } else {
      currentSearch.delete(event.target.name)
      localStorage.removeItem(appName + event.target.name)
    }

    if (cleanup) {
      cleanup.forEach((field) => {
        currentSearch.delete(field)
        localStorage.removeItem(appName + field)
      })
    }
    navigate({ search: '?' + currentSearch.toString() })
  }
  const handleToggleFilter = (event, cleanup, cleanSelf) => {
    currentSearch.set('_pagina', 1)

    const value = !(currentSearch.get(event.target.name) === 'true')
    currentSearch.set(event.target.name, value)
    if (cleanup) {
      cleanup.forEach((field) => {
        currentSearch.delete(field)
      })
    }
    if (cleanSelf && !value) {
      currentSearch.delete(event.target.name)
    }
    navigate({ search: '?' + currentSearch.toString() })
  }

  //Filter choice (applied unless component is submitted already assembled)
  const defaultComponent = (type, { cleanSelf, cleanup, ...item }) => {
    if (!item.name) {
      throw new Error('A nameless filter cannot be managed automatically via url')
    }
    switch (type) {
      case 'switch':
        return (
          <FilterSwitch
            {...{
              onClick: (event) => handleToggleFilter(event, cleanup, cleanSelf),
              checked: currentSearch.get(item.name) === 'true',
              ...item,
            }}
          />
        )
      case 'date':
        const { minDateField, maxDateField, ...newItem } = item
        return (
          <FilterDate
            {...{
              onChange: (event) => handleChangeFilter(event, cleanup),
              value: currentSearch.get(item.name) || item.default,
              minDate: currentSearch.get(minDateField),
              maxDate: currentSearch.get(maxDateField),
              ...newItem,
            }}
          />
        )
      case 'autocomplete':
        return (
          <FilterAutocomplete
            {...{
              onChange: (event) => handleChangeFilter(event, item.cleanup),
              value: currentSearch.get(item.name) || item.default,
              ...item,
            }}
          />
        )
      case 'basic-input':
        return (
          <FilterBasicInput
            {...{
              onChange: (event) => handleChangeFilter(event, item.cleanup),
              value: currentSearch.get(item.name) || item.default,
              ...item,
            }}
          />
        )
      case 'input':
      default:
        return (
          <FilterInput
            {...{
              onChange: (event) => handleChangeFilter(event, item.cleanup),
              value: currentSearch.get(item.name) || item.default,
              ...item,
            }}
          />
        )
    }
  }
  return filterComponent || defaultComponent(filterType, item)
}

export default FilterURL
